import { apiDomain, LocalSettings, websiteID, bt_env } from "../../settings";
import { getPremiumDetectionData } from "../data";
import { verifySignature, decodeBase64 } from "./verifySignature";
import { RL_PREVIEW } from "./queryParams";

export interface AllowlistState {
  status: boolean;
  source: string | null;
  oneCA: boolean;
}
export interface PremiumDetectionResult {
  isPremium: boolean;
  extensionInfo: {
    name: string;
    version: string;
    allowlistState: AllowlistState;
  } | null;
}

class PremiumABUserDetector {
  private singletonDetection?: Promise<PremiumDetectionResult>;
  private readonly premiumBypassModeEnabled: boolean;

  constructor(settings: LocalSettings) {
    this.premiumBypassModeEnabled = settings.premium_bypass_mode_enabled;
    this.singletonDetection = this.detectPremiumABUser();
  }

  public async detectPremiumABUser(): Promise<PremiumDetectionResult> {
    if (this.singletonDetection !== undefined) {
      return this.singletonDetection;
    }

    this.singletonDetection = new Promise<PremiumDetectionResult>((resolve) => {
      this.handlePreviousDetection(resolve);
    });

    return this.singletonDetection;
  }

  private handlePreviousDetection(
    resolve: (value: PremiumDetectionResult) => void,
  ) {
    if (RL_PREVIEW) {
      // prevent fetch signature on preview
      resolve({ isPremium: false, extensionInfo: null });
      return;
    }
    const previousDetection = getPremiumDetectionData();
    if (previousDetection) {
      resolve(previousDetection);
      return;
    }
    this.fetchAndHandlePayload(resolve);
  }

  private async fetchAndHandlePayload(
    resolve: (value: PremiumDetectionResult) => void,
  ) {
    let isResolved = false;

    const safeResolve = (result: PremiumDetectionResult) => {
      if (!isResolved) {
        resolve(result);
        isResolved = true;
      }
    };

    try {
      const response = await fetch(
        `https://${apiDomain}/mw/sign_pbm?w=${websiteID}&bt_env=${bt_env}`,
      );
      const detail = await response.json();

      const timeoutId = setTimeout(() => {
        safeResolve({ isPremium: false, extensionInfo: null });
        document.removeEventListener("flattr-payload", handlePayloadEvent);
      }, 1000);

      const handlePayloadEvent = async (
        event: CustomEvent<{
          payload: string;
          extensionInfo: {
            name: string;
            version: string;
            allowlistState: {
              status: boolean;
              source: string | null;
              oneCA: boolean;
            };
          };
        }>,
      ) => {
        clearTimeout(timeoutId);
        document.removeEventListener("flattr-payload", handlePayloadEvent);

        let isPremium: PremiumDetectionResult["isPremium"] = false;
        let extensionInfo: PremiumDetectionResult["extensionInfo"] = null;

        if (
          this.premiumBypassModeEnabled &&
          typeof event.detail.payload === "string"
        ) {
          const [encodedData, signature] = event.detail.payload.split(".");
          try {
            const isValidPremium = await verifySignature({
              encodedData,
              signature,
            });
            if (isValidPremium) {
              const {
                isPaying,
                expiresAt,
              }: { isPaying: boolean; expiresAt: number } = JSON.parse(
                decodeBase64(encodedData),
              );
              if (isPaying && Date.now() < Number(expiresAt)) {
                isPremium = true;
              }
            }
          } catch (error) {
            console.error("Error verifying premium signature:", error);
          }
        }

        if (event.detail.extensionInfo) {
          extensionInfo = event.detail.extensionInfo;
        }

        safeResolve({ isPremium, extensionInfo });
      };

      document.addEventListener(
        "flattr-payload",
        handlePayloadEvent as EventListener,
      );

      const event = new CustomEvent("flattr-request-payload", {
        detail,
      });
      document.dispatchEvent(event);
    } catch {
      safeResolve({ isPremium: false, extensionInfo: null });
    }
  }
}

let premiumAbUserDetector: PremiumABUserDetector | null = null;

export async function detectPremiumABUser(
  settings: LocalSettings,
): Promise<PremiumDetectionResult> {
  if (!premiumAbUserDetector) {
    premiumAbUserDetector = new PremiumABUserDetector(settings);
  }
  return premiumAbUserDetector.detectPremiumABUser();
}
